/**
 * Response schemas per route (from `@af/shared`), parsed in dev to surface contract drift
 * (warn, never throw). Lists come back as `Paginated<T>` (`{ items, nextCursor }`).
 */
import './zod-config';
import {
  ActivateVersionResult,
  AgentDelegationStatsResult,
  AgentDto,
  AgentVersionDto,
  AgentWorkspacesResult,
  ApprovalDto,
  ArtifactPreviewResult,
  AttachmentDto,
  AuditEntryDto,
  BillingDto,
  BillingPlansDto,
  BillingRedirectDto,
  BudgetDto,
  ClaudeLoginDto,
  CodexLoginResult,
  ConversationDto,
  EgressRequestDto,
  FlowAttachResult,
  FlowDto,
  FlowRunResult,
  FlowSuggestionsDto,
  FlowVersionDto,
  HaltResult,
  HealthDto,
  McpDiscoverResult,
  McpReposResult,
  McpServerDto,
  MeDto,
  MessageDto,
  ModelDto,
  NotificationDto,
  OnboardingStatusDto,
  OrgEventDto,
  OrgStatusDto,
  ProviderDto,
  ProviderTestResult,
  QuestionDto,
  RecordingDto,
  RecordingEventDto,
  RecordingIngestResult,
  type ROUTES,
  RunAnalysisReport,
  RunAnalysisReportSummary,
  RunAnalysisSettings,
  RunContextDto,
  RunDto,
  RunEventDto,
  SendMessageResult,
  SetupStatusDto,
  SkillDto,
  SkillExportDto,
  SkillSuggestionDto,
  SkillUsageDto,
  SkillVersionDto,
  SyncModelsResult,
  TaskDto,
  ToolPolicyDto,
  TriggerDto,
  TriggerFireDto,
  TriggerFireResult,
  UsageSummary,
} from '@af/shared';
import { z } from 'zod';

export type {
  AuditEntryDto,
  BudgetDto,
  MeDto,
  NotificationDto,
  OnboardingStatusDto,
  OrgStatusDto,
  RunEventDto,
  TaskDto,
} from '@af/shared';
export { SetupStatusDto };

/**
 * Org-level SSE payload. The API sends `{ kind, at }`; kinds are NOTIFY suffixes such as
 * `approval`, `halted`, `resumed` (worker adds `run`, `agent`, `task`, `notification`, …).
 * Optional ids are a proposed extension so the UI can patch instead of refetch.
 */
export const OrgEvent = OrgEventDto.partial({ at: true }).extend({
  agentId: z.string().optional(),
  runId: z.string().optional(),
  approvalId: z.string().optional(),
});
export type OrgEvent = z.infer<typeof OrgEvent>;

const list = <T extends z.ZodType>(item: T) =>
  z.object({ items: z.array(item), nextCursor: z.string().nullable() });

export const RESPONSE_SCHEMAS = {
  health: HealthDto,
  setupStatus: SetupStatusDto,
  billingStatus: BillingDto,
  billingPlans: BillingPlansDto,
  billingCheckout: BillingRedirectDto,
  billingPortal: BillingRedirectDto,
  me: MeDto,
  orgStatus: OrgStatusDto,
  onboardingStatus: OnboardingStatusDto,
  orgHalt: HaltResult,
  orgResume: HaltResult,
  listProviders: list(ProviderDto),
  createProvider: ProviderDto,
  updateProvider: ProviderDto,
  testProvider: ProviderTestResult,
  syncModels: SyncModelsResult,
  startCodexLogin: CodexLoginResult,
  pollCodexLogin: CodexLoginResult,
  disconnectCodex: ProviderDto,
  getClaudeLogin: z.object({
    available: z.boolean(),
    login: ClaudeLoginDto.nullable(),
    agents: z.array(z.object({ id: z.string(), name: z.string(), chosen: z.boolean() })),
  }),
  startClaudeLogin: z.object({ login: ClaudeLoginDto }),
  listModels: list(ModelDto),
  updateModel: ModelDto,
  overridePrice: ModelDto,
  listAgents: list(AgentDto),
  getAgent: AgentDto,
  createAgent: AgentDto,
  updateAgent: AgentDto,
  listAgentVersions: list(AgentVersionDto),
  createAgentVersion: AgentVersionDto,
  listAgentWorkspaces: AgentWorkspacesResult,
  agentDelegationStats: AgentDelegationStatsResult,
  activateAgentVersion: ActivateVersionResult,
  listConversations: list(ConversationDto),
  createConversation: ConversationDto,
  getMessages: list(MessageDto),
  getChatFallback: z.object({ first: z.object({ runId: z.string(), seq: z.number() }).nullable() }),
  sendMessage: SendMessageResult,
  uploadAttachment: AttachmentDto,
  artifactPreview: ArtifactPreviewResult,
  listRuns: list(RunDto),
  getRun: RunDto,
  runEvents: list(RunEventDto),
  cancelRun: RunDto,
  continueRun: RunDto,
  restartRun: RunDto,
  listTasks: list(TaskDto),
  createTask: TaskDto,
  listApprovals: list(ApprovalDto),
  listQuestions: list(QuestionDto),
  listEgressRequests: list(EgressRequestDto),
  decideEgressRequest: EgressRequestDto,
  decideApproval: ApprovalDto,
  listRunAnalysisReports: list(RunAnalysisReportSummary),
  getRunAnalysisReport: RunAnalysisReport,
  runRunAnalysis: RunAnalysisReport,
  runAnalysisSettings: RunAnalysisSettings,
  updateRunAnalysisSettings: RunAnalysisSettings,
  usage: UsageSummary,
  listBudgets: list(BudgetDto),
  upsertBudget: BudgetDto,
  listNotifications: list(NotificationDto),
  auditLog: list(AuditEntryDto),
  answerRun: RunDto,
  listTriggers: list(TriggerDto),
  getTrigger: TriggerDto,
  createTrigger: TriggerDto,
  updateTrigger: TriggerDto,
  fireTrigger: TriggerFireResult,
  listTriggerFires: list(TriggerFireDto),
  listMcpServers: list(McpServerDto),
  createMcpServer: McpServerDto,
  updateMcpServer: McpServerDto,
  discoverMcpServer: McpDiscoverResult,
  listMcpRepos: McpReposResult,
  listToolPolicies: list(ToolPolicyDto),
  replaceToolPolicies: list(ToolPolicyDto),
  listRecordings: list(RecordingDto),
  createRecording: RecordingDto,
  getRecording: RecordingDto,
  listRecordingEvents: z.object({ items: z.array(RecordingEventDto) }),
  ingestRecordingEvents: RecordingIngestResult,
  stopRecording: RecordingDto,
  synthesizeRecording: RecordingDto,
  listFlows: list(FlowDto),
  getFlow: FlowDto,
  createFlow: FlowDto,
  updateFlow: FlowDto,
  listFlowVersions: list(FlowVersionDto),
  createFlowVersion: FlowDto,
  suggestFlowAgents: FlowSuggestionsDto,
  attachFlow: FlowAttachResult,
  runFlow: FlowRunResult,
  getRunContext: RunContextDto,
  listSkills: list(SkillDto),
  getSkill: SkillDto,
  createSkill: SkillDto,
  importSkill: SkillDto,
  exportSkill: SkillExportDto,
  listSkillVersions: list(SkillVersionDto),
  createSkillVersion: SkillDto,
  activateSkill: SkillDto,
  rejectSkill: SkillDto,
  listSkillSuggestions: list(SkillSuggestionDto),
  listSkillUsage: list(SkillUsageDto),
  acceptSkill: SkillDto,
} satisfies Partial<Record<keyof typeof ROUTES, z.ZodType>>;
